import React, { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { useNem12BackgroundPolling } from "@/features/NEM12/hooks/useNem12BackgroundPolling";
import Layout from "../components/Layouts/Layout";
import ProtectedRoute from "../shared/helper/ProtectedRoute";
import { useApiLoader } from "@/shared/context/ApiLoaderContext";
import setupAxiosLoader from "@/shared/helper/setupAxiosLoader";
import axiosInstance from "@/shared/services/axiosInstance";
import {
  Contact,
  // Dashboard,
  DataValidationReport,
  MeterGroup,
  NMIGenerator,
  NotFound,
  ReportENem12,
  ReportHistory,
  ReportMdn,
  ReportMeterEvent,
  ReportNem12,
  ReportNem12QualityFlag,
  ReportOrionCumulative,
  ReportSiteUsage,
  Site,
  User,
  ValidationHistory,
  SerialGenerator,
  PropertyGenerator,
  ValidationSummary,
  AdvancedEcoValidation,
  IotDataSummary,
  AnomalyDashboard,
  CompanySettings,
  IotSync,
  CetSync,
  AdvancedEcoSync,
  DataCorrections,
  PlatformConfiguration,
  DeviceStatus,
  Meters,
  VirtualMeter,
  UserAuditLogs,
  LogMaintenance,
  SystemLogs,
  MeterStatusCodes,
  Role,
  Dashboard,
  LowBatteryMeters,
  OfflineMeters,
  MetrixInsights,
  SupersetConfiguration,
  ImportMeters,
  UnprovisionedMeters,
  UnprovisionedIotDevices,
  MeterDataAudit,
  AlarmEvents,
  AlarmProperty,
  MeterSchedules,
  ContactSupport,
  HeaderContactSupport,
  Profile,
  NotificationLog,
  Authentication,
  NEM12Viewer,
} from "./routeLoaders";
import {
  Modules,
  ROUTES,
  PermissionsForRoute,
  NotFoundPath,
  UnauthorizedPath,
} from "@/shared/constants";
import PrivateRoute from "@/shared/helper/PrivateRoute";

// Must be rendered inside BrowserRouter so useLocation is available
const Nem12BackgroundPolling = (): null => {
  useNem12BackgroundPolling();
  return null;
};

const AppRoutes = (): React.ReactElement => {
  const { start, stop } = useApiLoader();

  useEffect(() => {
    setupAxiosLoader(axiosInstance, start, stop);
  }, [start, stop]);
  return (
    <BrowserRouter>
      <Nem12BackgroundPolling />
      <Routes>
        <Route element={<ProtectedRoute />}>
          <Route element={<Layout />}>
            <Route
              path={ROUTES.dashboard.root}
              element={
                <PrivateRoute
                  module={Modules.dashboardPage}
                  permission={PermissionsForRoute.view}
                >
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.dashboard.lowBatteryMeters}
              element={
                <PrivateRoute
                  module={Modules.dashboardPage}
                  permission={PermissionsForRoute.view}
                >
                  <LowBatteryMeters />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.dashboard.offlineMeters}
              element={
                <PrivateRoute
                  module={Modules.dashboardPage}
                  permission={PermissionsForRoute.view}
                >
                  <OfflineMeters />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.dashboard.metrixInsights}
              element={
                <PrivateRoute
                  module={Modules.metrixInsights}
                  permission={PermissionsForRoute.view}
                >
                  <MetrixInsights />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.header.superset.configuration}
              element={
                <PrivateRoute
                  module={Modules.metrixInsights}
                  permission={PermissionsForRoute.view}
                >
                  <SupersetConfiguration />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.management.meters}
              element={
                <PrivateRoute
                  module={Modules.meters}
                  permission={PermissionsForRoute.view}
                >
                  <Meters />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.management.virtualMeters}
              element={
                <PrivateRoute
                  module={Modules.virtualMeters}
                  permission={PermissionsForRoute.view}
                >
                  <VirtualMeter />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.management.meterGroups}
              element={
                <PrivateRoute
                  module={Modules.meterGroups}
                  permission={PermissionsForRoute.view}
                >
                  <MeterGroup />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.management.sites}
              element={
                <PrivateRoute
                  module={Modules.sites}
                  permission={PermissionsForRoute.view}
                >
                  <Site />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.management.users}
              element={
                <PrivateRoute
                  module={Modules.users}
                  permission={PermissionsForRoute.view}
                >
                  <User />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.management.roles}
              element={
                <PrivateRoute
                  module={Modules.roles}
                  permission={PermissionsForRoute.view}
                >
                  <Role />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.management.contacts}
              element={
                <PrivateRoute
                  module={Modules.contacts}
                  permission={PermissionsForRoute.view}
                >
                  <Contact />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.management.companySettings}
              element={
                <PrivateRoute
                  module={Modules.companySettings}
                  permission={PermissionsForRoute.view}
                >
                  <CompanySettings />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.header.acknowledge}
              element={<NotificationLog />}
            />
            <Route
              path={ROUTES.reportingSetup.enem12}
              element={
                <PrivateRoute
                  module={Modules.enem12}
                  permission={PermissionsForRoute.view}
                >
                  <ReportENem12 />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.reportingSetup.nem12}
              element={
                <PrivateRoute
                  module={Modules.nem12}
                  permission={PermissionsForRoute.view}
                >
                  <ReportNem12 />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.reportingSetup.mdn}
              element={
                <PrivateRoute
                  module={Modules.mdn}
                  permission={PermissionsForRoute.view}
                >
                  <ReportMdn />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.reportingSetup.nem12QualityFlag}
              element={
                <PrivateRoute
                  module={Modules.qualityFlags}
                  permission={PermissionsForRoute.view}
                >
                  <ReportNem12QualityFlag />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.reportingSetup.meterEvent}
              element={
                <PrivateRoute
                  module={Modules.meterEvents}
                  permission={PermissionsForRoute.view}
                >
                  <ReportMeterEvent />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.reportingSetup.orionCumulative}
              element={
                <PrivateRoute
                  module={Modules.orion}
                  permission={PermissionsForRoute.view}
                >
                  <ReportOrionCumulative />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.reportingSetup.siteUsage}
              element={
                <PrivateRoute
                  module={Modules.siteUsage}
                  permission={PermissionsForRoute.view}
                >
                  <ReportSiteUsage />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.reportingSetup.validation}
              element={
                <PrivateRoute
                  module={Modules.validationRules}
                  permission={PermissionsForRoute.view}
                >
                  <DataValidationReport />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.reporting.view}
              element={
                <PrivateRoute
                  module={Modules.reportHistory}
                  permission={PermissionsForRoute.view}
                >
                  <ReportHistory />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.reporting.validationHistory}
              element={
                <PrivateRoute
                  module={Modules.validationHistory}
                  permission={PermissionsForRoute.view}
                >
                  <ValidationHistory />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.monitoring.deviceStatus}
              element={
                <PrivateRoute
                  module={Modules.deviceStatus}
                  permission={PermissionsForRoute.view}
                >
                  <DeviceStatus />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.monitoring.meterSchedules}
              element={
                <PrivateRoute
                  module={Modules.meterSchedules}
                  permission={PermissionsForRoute.view}
                >
                  <MeterSchedules />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.dataTools.dataCorrections}
              element={
                <PrivateRoute
                  module={Modules.dataCorrections}
                  permission={PermissionsForRoute.view}
                >
                  <DataCorrections />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.dataTools.serialGenerator}
              element={
                <PrivateRoute
                  module={Modules.serialNumberGenerator}
                  permission={PermissionsForRoute.view}
                >
                  <SerialGenerator />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.reporting.validationSummary}
              element={
                <PrivateRoute
                  module={Modules.validationSummary}
                  permission={PermissionsForRoute.view}
                >
                  <ValidationSummary />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.reporting.advancedEco}
              element={
                <PrivateRoute
                  module={Modules.advancedEcoValidation}
                  permission={PermissionsForRoute.view}
                >
                  <AdvancedEcoValidation />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.reporting.iotData}
              element={
                <PrivateRoute
                  module={Modules.iotDataSummary}
                  permission={PermissionsForRoute.view}
                >
                  <IotDataSummary />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.reporting.anomalyDashboard}
              element={
                <PrivateRoute
                  module={Modules.reporting}
                  permission={PermissionsForRoute.view}
                >
                  <AnomalyDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.dataTools.propertyGenerator}
              element={
                <PrivateRoute
                  module={Modules.propertyNumberGenerator}
                  permission={PermissionsForRoute.view}
                >
                  <PropertyGenerator />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.dataTools.nmiGenerator}
              element={
                <PrivateRoute
                  module={Modules.nmiGenerator}
                  permission={PermissionsForRoute.view}
                >
                  <NMIGenerator />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.dataTools.nem12}
              element={
                <PrivateRoute
                  module={Modules.nem12Viewer}
                  permission={PermissionsForRoute.view}
                >
                  <NEM12Viewer />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.integrations.iotDataSync}
              element={
                <PrivateRoute
                  module={Modules.iotSync}
                  permission={PermissionsForRoute.view}
                >
                  <IotSync />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.integrations.cetDataSync}
              element={
                <PrivateRoute
                  module={Modules.cetSync}
                  permission={PermissionsForRoute.view}
                >
                  <CetSync />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.integrations.advancedEcoDataSync}
              element={
                <PrivateRoute
                  module={Modules.advancedEcoSync}
                  permission={PermissionsForRoute.view}
                >
                  <AdvancedEcoSync />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.system.configuration}
              element={
                <PrivateRoute
                  module={Modules.platformConfiguration}
                  permission={PermissionsForRoute.view}
                >
                  <PlatformConfiguration />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.logs.userAuditLogs}
              element={
                <PrivateRoute
                  module={Modules.userAuditLogs}
                  permission={PermissionsForRoute.view}
                >
                  <UserAuditLogs />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.logs.maintenance}
              element={
                <PrivateRoute
                  module={Modules.logMaintenance}
                  permission={PermissionsForRoute.view}
                >
                  <LogMaintenance />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.logs.systemLogs}
              element={
                <PrivateRoute
                  module={Modules.systemLogs}
                  permission={PermissionsForRoute.view}
                >
                  <SystemLogs />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.system.meterStatusCodes}
              element={
                <PrivateRoute
                  module={Modules.meterStatusCodes}
                  permission={PermissionsForRoute.view}
                >
                  <MeterStatusCodes />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.system.contactSupport}
              element={
                <PrivateRoute
                  module={Modules.support}
                  permission={PermissionsForRoute.view}
                >
                  <ContactSupport />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.header.contactSupport}
              element={<HeaderContactSupport />}
            />
            <Route path={ROUTES.header.profile} element={<Profile />} />
            <Route
              path={ROUTES.header.authentication}
              element={<Authentication />}
            />
            <Route
              path={ROUTES.alarmsSetup.meterAlarmEvent}
              element={
                <PrivateRoute
                  module={Modules.alarmEvents}
                  permission={PermissionsForRoute.view}
                >
                  <AlarmEvents />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.provisioning.importMeters}
              element={
                <PrivateRoute
                  module={Modules.importMeters}
                  permission={PermissionsForRoute.view}
                >
                  <ImportMeters />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.provisioning.unprovisionedMeters}
              element={
                <PrivateRoute
                  module={Modules.unprovisionedMeters}
                  permission={PermissionsForRoute.view}
                >
                  <UnprovisionedMeters />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.provisioning.unprovisionedIotDevices}
              element={
                <PrivateRoute
                  module={Modules.unprovisionedIoTDevices}
                  permission={PermissionsForRoute.view}
                >
                  <UnprovisionedIotDevices />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.monitoring.meterDataAudit}
              element={
                <PrivateRoute
                  module={Modules.meterDataAudit}
                  permission={PermissionsForRoute.view}
                >
                  <MeterDataAudit />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.alarmsSetup.iotPayload}
              element={
                <PrivateRoute
                  module={Modules.alarmProperty}
                  permission={PermissionsForRoute.view}
                >
                  <AlarmProperty />
                </PrivateRoute>
              }
            />
          </Route>
        </Route>

        <Route path={ROUTES.notFound} element={<NotFound />} />
        <Route path={UnauthorizedPath} element={<NotFound />} />
        <Route path="/404" element={<NotFound />} />

        <Route
          path={NotFoundPath}
          element={<Navigate to={ROUTES.notFound} replace />}
        />
      </Routes>
    </BrowserRouter>
  );
};

export default AppRoutes;
